import { Link, useLocation } from "react-router-dom";
import {
  Activity, ArrowUpDown, Building2, CheckCircle, Code, Container, FileText, Home, KeyRound,
  Landmark, PanelLeftClose, PanelLeftOpen, RefreshCw, ScrollText, Server, Settings, Shield,
  ShieldCheck, UserCog, Users, Wallet, type LucideIcon,
} from "lucide-react";
import { BrandMark } from "@/components/shell/brand-logo";
import { Avatar } from "@/components/ui/avatar";
import type { NavSection } from "@/lib/constants";
import { useAuthStore } from "@/stores/auth-store";
import { cn } from "@/lib/utils";

const ICONS: Record<string, LucideIcon> = {
  Home, Wallet, ArrowUpDown, CheckCircle, Shield, Activity, RefreshCw, FileText, Users, Container,
  Code, Landmark, KeyRound, Settings, UserCog, Server, ScrollText, Building2, ShieldCheck,
};

export default function Sidebar({
  sections,
  onNavigate,
  collapsed,
  onToggleCollapse,
}: {
  sections: NavSection[];
  onNavigate?: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const pathname = useLocation().pathname;
  const orgLabel = useAuthStore((s) => s.experience.activeOrganization.label);
  const orgInitials = orgLabel.replace(/[^A-Za-z0-9 ]/g, "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "—";
  return (
    <nav
      aria-label="Sidebar"
      className={cn(
        "flex h-full shrink-0 flex-col overflow-y-auto bg-sidebar-bg text-text transition-[width] duration-150",
        collapsed ? "w-[72px]" : "w-[272px]"
      )}
    >
      <div
        className={cn(
          "flex items-center",
          collapsed ? "flex-col gap-2.5 px-2 pb-[18px] pt-[22px]" : "min-h-[52px] gap-3 px-[18px] pb-[18px] pt-[22px]"
        )}
      >
        <BrandMark size={32} />
        {!collapsed && (
          <div className="min-w-0 flex-1 leading-tight">
            <div className="text-[20px] font-extrabold leading-tight tracking-tight text-ink">Alloy</div>
          </div>
        )}
        <button
          type="button"
          onClick={onToggleCollapse}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          data-testid="sidebar-toggle"
          className="grid size-8 shrink-0 place-items-center rounded-md text-muted transition-colors hover:bg-disabled-soft hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft"
        >
          {collapsed ? <PanelLeftOpen aria-hidden className="size-5" /> : <PanelLeftClose aria-hidden className="size-5" />}
        </button>
      </div>

      <div className={cn("flex-1 pb-3 pt-1", collapsed ? "px-2" : "px-4")}>
        {sections.map((section) => (
          <div key={section.label}>
            {!collapsed && (
              <div className="px-2 pb-1.5 pt-4 text-sm first:pt-2 font-medium text-text">{section.label}</div>
            )}
            <ul className={collapsed ? "pt-2 first:pt-0" : undefined}>
              {section.items.map((item) => {
                const Icon = ICONS[item.icon] ?? Shield;
                const base = item.href === "/wallets" ? "/wallets" : item.href.split("/")[1];
                const active = pathname === item.href || pathname.startsWith("/" + base);
                return (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      onClick={() => onNavigate?.()}
                      aria-current={active ? "page" : undefined}
                      title={collapsed ? item.label : undefined}
                      className={cn(
                        "relative flex h-9 w-full items-center gap-2 rounded-md text-left text-sm font-medium text-text transition-colors hover:bg-disabled-soft hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft",
                        collapsed ? "justify-center px-0" : "px-2",
                        active && "bg-disabled-soft font-semibold text-ink"
                      )}
                    >
                      <Icon aria-hidden className="size-5 shrink-0" />
                      {!collapsed && <span className="truncate">{item.label}</span>}
                      {item.badge ? (
                        <span
                          className={cn(
                            "grid h-[18px] min-w-[18px] place-items-center rounded-full bg-danger-100 px-1 text-xs font-extrabold text-danger",
                            collapsed ? "absolute right-0.5 top-0.5" : "ml-auto"
                          )}
                        >
                          {item.badge}
                        </span>
                      ) : null}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      <div className={cn("border-t border-line/60", collapsed ? "px-2 py-3" : "px-4 py-3")}>
        <div className={cn("flex items-center", collapsed ? "justify-center" : "gap-2.5")}>
          <span title={orgLabel}>
            <Avatar initials={orgInitials} bgColor="var(--ads-org-avatar)" size={28} fontSize={10} aria-label={`${orgLabel} logo`} />
          </span>
          {!collapsed && (
            <div className="min-w-0 flex-1 leading-tight">
              <div className="truncate text-xs font-semibold text-ink">{orgLabel}</div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}